import { Routes, Route, Navigate, Link } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { SignInGate } from './components/SignInGate'
import { Dashboard } from './pages/Dashboard'
import { EventsList } from './pages/EventsList'
import { EventDetail } from './pages/EventDetail'
import { EventAvailability } from './pages/EventAvailability'
import { Placeholder } from './pages/Placeholder'
import { AdminChecklistTemplates } from './pages/AdminChecklistTemplates'
import { AdminHazardLibrary } from './pages/AdminHazardLibrary'
import { AdminBriefingTopics } from './pages/AdminBriefingTopics'
import { AdminLibraryComponents } from './pages/AdminLibraryComponents'
import { AdminLibraryActivities } from './pages/AdminLibraryActivities'
import { AdminLibraryRoles } from './pages/AdminLibraryRoles'
import { AdminTicketTypes } from './pages/AdminTicketTypes'
import { AdminAudit } from './pages/AdminAudit'
import { Reports } from './pages/Reports'
import { ClaudeHelp } from './pages/ClaudeHelp'
import { Flags } from './pages/Flags'
import { Volunteers } from './pages/Volunteers'
import { EventPlanPrint } from './pages/EventPlanPrint'
import { EventPlanDoc } from './pages/EventPlanDoc'
import { EventsReport } from './pages/EventsReport'
import { ServiceTimetableDoc } from './pages/ServiceTimetableDoc'
import { ServiceTimetableReport } from './pages/ServiceTimetableReport'
import { CateringReport } from './pages/CateringReport'
import { EventProductBriefing } from './pages/EventProductBriefing'
import { LiveDayView } from './pages/LiveDayView'
import { SignIn } from './pages/SignIn'
import { Calendar } from './pages/Calendar'
import { MyActions } from './pages/MyActions'
import { AdminUsers } from './pages/AdminUsers'
import { AdminTemplates } from './pages/AdminTemplates'
import { Archive } from './pages/Archive'
import { ResourcesDirectory } from './pages/ResourcesDirectory'
import { PublicPagesDirectory } from './pages/PublicPagesDirectory'
import { ForecastStudioPage } from './pages/ForecastStudioPage'
import { AdminLibraryFunctions } from './pages/AdminLibraryFunctions'
import { FunctionConfirmPage } from './pages/FunctionConfirmPage'
import { useMe } from './hooks/useMe'

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe()
  if (!me?.isAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

// AdminLanding - simple index of admin sub-pages. Users / permissions /
// audit each get their own placeholders until spec 15 / spec 08 ship.
function AdminLanding() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl mb-1">Admin</h1>
        <p className="text-ink-soft">Manage templates, users, and audit history.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AdminCard
          to="/admin/checklist-templates"
          title="Checklist Templates"
          description="Reusable checklists event managers apply per event."
        />
        <AdminCard
          to="/admin/users"
          title="Users & permissions"
          description="Directory of every person known to the app, sourced from Entra."
        />
        <AdminCard
          to="/admin/audit"
          title="Audit log"
          description="Every mutation across the system, newest first."
        />
      </div>
    </div>
  )
}

function AdminCard({ to, title, description }: { to: string; title: string; description: string }) {
  return (
    <Link
      to={to}
      className="block bg-white border border-line rounded p-5 hover:border-brass hover:shadow-md transition-all"
    >
      <h2 className="font-display text-lg mb-1">{title}</h2>
      <p className="text-sm text-ink-soft">{description}</p>
    </Link>
  )
}

export default function App() {
  return (
    <SignInGate>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Dashboard />} />
          <Route path="events" element={<EventsList />} />
          <Route path="events/:id" element={<EventDetail />} />
          <Route path="events/:id/availability" element={<EventAvailability />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="my-actions" element={<MyActions />} />
          <Route path="reports" element={<Reports />} />
          <Route path="claude-help" element={<ClaudeHelp />} />
          <Route path="flags" element={<Flags />} />
          <Route path="volunteers" element={<Volunteers />} />
          {/* Forecast Studio — model an Idea's viability before commit. Admin-gated. */}
          <Route path="forecast" element={<AdminRoute><ForecastStudioPage /></AdminRoute>} />
          <Route path="forecast/:id" element={<AdminRoute><ForecastStudioPage /></AdminRoute>} />
          <Route
            path="admin"
            element={
              <AdminRoute>
                <AdminLanding />
              </AdminRoute>
            }
          />
          <Route
            path="admin/checklist-templates"
            element={
              <AdminRoute>
                <AdminChecklistTemplates />
              </AdminRoute>
            }
          />
          <Route
            path="admin/audit"
            element={
              <AdminRoute>
                <AdminAudit />
              </AdminRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            }
          />
          <Route
            path="admin/templates"
            element={
              <AdminRoute>
                <AdminTemplates />
              </AdminRoute>
            }
          />
          <Route
            path="archive"
            element={
              <AdminRoute>
                <Archive />
              </AdminRoute>
            }
          />
          <Route path="resources" element={<ResourcesDirectory />} />
          {/* Per-library placeholder routes — sidebar surfaces these as
              "coming soon" entries so Chris can see the planned MVP shape.
              Each will swap to a real admin surface in a future spec. */}
          <Route
            path="resources/activities"
            element={
              <AdminRoute>
                <AdminLibraryActivities />
              </AdminRoute>
            }
          />
          <Route
            path="resources/components"
            element={
              <AdminRoute>
                <AdminLibraryComponents />
              </AdminRoute>
            }
          />
          <Route
            path="resources/risks"
            element={
              <AdminRoute>
                <AdminHazardLibrary />
              </AdminRoute>
            }
          />
          <Route
            path="resources/roles"
            element={
              <AdminRoute>
                <AdminLibraryRoles />
              </AdminRoute>
            }
          />
          <Route
            path="resources/ticket-types"
            element={
              <AdminRoute>
                <AdminTicketTypes />
              </AdminRoute>
            }
          />
          <Route
            path="resources/functions"
            element={
              <AdminRoute>
                <AdminLibraryFunctions />
              </AdminRoute>
            }
          />
          <Route
            path="admin/templates/briefing-topics"
            element={
              <AdminRoute>
                <AdminBriefingTopics />
              </AdminRoute>
            }
          />
          <Route path="public-pages" element={<PublicPagesDirectory />} />
          <Route path="*" element={<Placeholder title="Not found" subtitle="That page does not exist" />} />
        </Route>
        {/* Print-only routes render outside the AppShell so the browser
            "Save as PDF" dialog gets a clean page. */}
        <Route path="events/:id/print" element={<EventPlanPrint />} />
        {/* Production Plan (full, internal) vs Event Plan (public summary) +
            the all-events report — all browser-print, shell-less (item 14/15). */}
        <Route path="events/:id/event-plan" element={<EventPlanDoc />} />
        <Route path="events-report" element={<EventsReport />} />
        {/* Event Service Timetable — Timings & Set Plan (Feature 2). */}
        <Route path="events/:id/service-timetable" element={<ServiceTimetableDoc />} />
        <Route path="service-timetable-report" element={<ServiceTimetableReport />} />
        {/* Global catering PDF (Feature 3). */}
        <Route path="catering-report" element={<CateringReport />} />
        {/* Product Briefing PDF (Feature 1). */}
        <Route path="events/:id/product-briefing" element={<EventProductBriefing />} />
        {/* Live Day View — full-screen dark surface, mounted outside
            the AppShell so the header sits flush against the viewport
            edge (no sidebar, no breadcrumbs). PR L2 ships a
            placeholder; PR L3 replaces it with the full implementation. */}
        <Route path="live/:eventId" element={<LiveDayView />} />
        {/* The old Events volunteer portal (magic-link SPA) is retired — the
            volunteer experience now lives in InfoHub (Events Live build). */}
        <Route path="auth/sign-in" element={<SignIn />} />
        {/* Function-confirm landing (EM-2) — reached by an emailed magic link,
            token-authed, no session. Shell-less; exempted in SignInGate. */}
        <Route path="forecast/function/:token" element={<FunctionConfirmPage />} />
      </Routes>
    </SignInGate>
  )
}